import { HttpErrorResponse } from '@angular/common/http';
import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import {
  STUDY_KPI_CATEGORIES_TO_KEYS_MAPPING,
  STUDY_KPI_KEYS_TO_NAMES_MAP,
  STUDY_KPI_KEYS_TO_TYPE_MAPPING
} from '@catalogues';
import {
  Kpi,
  KpiCategories,
  KpiConfig,
  KpiKeys,
  KpisByCategory,
  KpisByKey,
  Message,
  MilestoneName,
  ResponseState,
  Segment,
  StageName,
  StudyMilestones
} from '@models';
import {
  CommonFunctionsService,
  ExpandedPanelStateService,
  KpiFilterService,
  KpiService,
  SidebarService,
  SidePanelStateService,
  StudyInsightsService,
  StudyService
} from '@services';
import { catchError, forkJoin, map, Observable, of } from 'rxjs';

import { featureFlags } from '../../../environments/environment';

@Component({
  selector: 'study-details',
  templateUrl: './study-details.component.html',
  styleUrl: './study-details.component.scss'
})
export class StudyDetailsComponent implements OnInit, OnDestroy {
  private studyService: StudyService = inject(StudyService);
  private kpiService: KpiService = inject(KpiService);
  private route: ActivatedRoute = inject(ActivatedRoute);
  private sidebarService: SidebarService = inject(SidebarService);

  private kpiFilterService: KpiFilterService = inject(KpiFilterService);
  private sidePanelStateService: SidePanelStateService = inject(SidePanelStateService);
  private expandedPanelStateService: ExpandedPanelStateService = inject(ExpandedPanelStateService);

  private commonFunctionsService: CommonFunctionsService = inject(CommonFunctionsService);
  private studyInsightsService: StudyInsightsService = inject(StudyInsightsService);

  pageTitle = 'Study --';
  protocolNumber?: string;
  studyMilestonesRes!: ResponseState<StudyMilestones>;
  filteredKpisByCategory: KpisByCategory | undefined = undefined;
  expandedGanttChartStages!: StageName[];
  allKpisByCategory!: KpisByCategory;
  studySk!: string;
  kpiConfig: KpiConfig = {};
  studySummary: Observable<string | Segment[]> = of('Loading...');

  sidebarCollapsed!: boolean;
  sidePanelExpanded!: boolean;
  isLoading = true;

  selectedKpiByKey?: { key: KpiKeys; kpi: Kpi };
  notAllowedError = false;
  browserTimezone = this.commonFunctionsService.getBrowserTimezone();
  keyMilestones: MilestoneName[] = [];

  async ngOnInit() {
    this.studySk = this.route.snapshot.paramMap.get('studySk') || '';
    const milestones$ = this.studyService.getMilestones(this.studySk).pipe(
      map((data) => ({ status: 'success', data }) as const),
      catchError((err: HttpErrorResponse) => {
        return of({ status: 'error', errorCode: err.status } as const);
      })
    );

    const kpiKeys: KpiKeys[] = Object.keys(STUDY_KPI_KEYS_TO_NAMES_MAP) as KpiKeys[];

    const kpiByCategoryResponses = this.getEmptyKpiByCategoryResponsesObject();
    const kpis$ = this.kpiService.getKpiAll(this.studySk, kpiKeys, this.browserTimezone).pipe(
      map((data) => {
        const kpisByKeyResponse = data as KpisByKey;
        Object.keys(STUDY_KPI_CATEGORIES_TO_KEYS_MAPPING).forEach((categoryKey) => {
          const category = categoryKey as KpiCategories;
          STUDY_KPI_CATEGORIES_TO_KEYS_MAPPING[category]?.forEach((kpiKey) => {
            if (Object.prototype.hasOwnProperty.call(kpisByKeyResponse, kpiKey)) {
              kpiByCategoryResponses[category]![kpiKey] = kpisByKeyResponse[kpiKey];
            }
          });
        });
        return { status: 'success', data: kpiByCategoryResponses } as const;
      }),
      catchError((err: HttpErrorResponse) => {
        this.allKpisByCategory = kpiByCategoryResponses;
        return of({ status: 'error', errorCode: err.status } as const);
      })
    );

    const kpiConfig$ = this.kpiService.getKpiConfig().pipe(
      map((data) => ({ status: 'success', data }) as const),
      catchError((err: HttpErrorResponse) => {
        return of({ status: 'error', errorCode: err.status } as const);
      })
    );

    this.joinEndpointCalls(milestones$, kpis$, kpiConfig$);

    this.sidebarService.sidebarCollapsed$.subscribe((collapsed) => {
      this.sidebarCollapsed = collapsed;
    });

    this.sidePanelStateService.sidePanelExpanded$.subscribe((expanded) => {
      this.sidePanelExpanded = expanded;
    });
  }

  ngOnDestroy() {
    if (this.sidePanelExpanded) this.sidePanelStateService.setSidePanelExpanded(false);
    if (this.expandedPanelStateService.getExpandedPanelState())
      this.expandedPanelStateService.setExpandedPanelExpanded(false);
  }

  getKeyMilestones(): MilestoneName[] {
    if (this.studyMilestonesRes && this.studyMilestonesRes.status !== 'success') return [];
    const keyMilestones: MilestoneName[] = [];
    this.studyMilestonesRes.data.stages
      .flatMap((stage) =>
        stage.milestones.flatMap((item) => (item.type === 'group' ? item.milestones : [item]))
      )
      .forEach((milestone) => {
        if (milestone.isKeyMilestone) {
          keyMilestones.push(milestone.name);
        }
      });

    return keyMilestones;
  }

  onExpandedStagesChange(stageNames: StageName[]) {
    Promise.resolve().then(() => {
      this.expandedGanttChartStages = stageNames;
      this.filteredKpisByCategory = this.kpiFilterService.filterKpisByStage(
        stageNames,
        this.allKpisByCategory
      );
    });
  }

  onKpiCardClicked(kpiKey: KpiKeys) {
    const clickedKpi: Kpi | undefined = this.getKpiByKey(kpiKey);
    if (!clickedKpi || !this.kpiHasValidValue(clickedKpi)) return;

    if (this.selectedKpiByKey?.key == kpiKey) {
      this.onSidePanelClosed();
      return;
    }

    this.selectedKpiByKey = { key: kpiKey, kpi: clickedKpi };
    if (!this.sidePanelExpanded) this.sidePanelStateService.setSidePanelExpanded(true);
  }

  onSidePanelClosed(): void {
    this.selectedKpiByKey = undefined;
    this.sidePanelStateService.setSidePanelExpanded(false);
  }

  onExpandedPanelOpened() {
    this.expandedPanelStateService.setExpandedPanelExpanded(true);
  }

  private joinEndpointCalls(
    milestones$: Observable<ResponseState<StudyMilestones>>,
    kpis$: Observable<ResponseState<KpisByCategory>>,
    kpiConfig$: Observable<ResponseState<KpiConfig>>
  ) {
    forkJoin([milestones$, kpis$, kpiConfig$]).subscribe({
      next: ([milestones, kpis, kpiConfig]) => {
        if (milestones.status == 'success') {
          this.protocolNumber = milestones.data.studyNumber.protocolNumber;
          this.pageTitle = `Study ${this.protocolNumber}`;
        }

        if (kpis.status == 'success') this.allKpisByCategory = kpis.data;
        if (kpiConfig.status == 'success') this.kpiConfig = kpiConfig.data;

        this.checkNotAllowedError(milestones, kpis);
        this.studyMilestonesRes = milestones;
        this.keyMilestones = this.getKeyMilestones();

        if (featureFlags.enableStudyInsights && milestones.status == 'success') {
          this.studySummary = this.studyInsightsService
            .getStudyInsights(milestones.data, this.allKpisByCategory)
            .pipe(
              map((insights) => {
                return this.mapResponseState(insights);
              })
            );
        }
        this.isLoading = false;
      }
    });
  }

  private mapResponseState(
    insights: { status: 'success'; data: Message[] } | { status: 'error'; errorCode: number }
  ) {
    if (insights.status === 'success') {
      return insights.data[1]?.content ?? 'No study insights available at this time.';
    } else {
      return 'No study insights available at this time.';
    }
  }

  private checkNotAllowedError(...responses: ResponseState<any>[]): void {
    if (responses.some((r) => r.status === 'error' && r.errorCode === 401)) {
      this.notAllowedError = true;
    }
  }

  private getEmptyKpiByCategoryResponsesObject(): KpisByCategory {
    const emptyResponses: KpisByCategory = {} as KpisByCategory;
    (Object.keys(STUDY_KPI_CATEGORIES_TO_KEYS_MAPPING) as KpiCategories[]).forEach(
      (categoryKey) => {
        emptyResponses[categoryKey] = {};

        (
          Object.values(STUDY_KPI_CATEGORIES_TO_KEYS_MAPPING[categoryKey] || {}) as KpiKeys[]
        ).forEach((kpiKey) => {
          emptyResponses[categoryKey]![kpiKey] = this.getNoDataKpi(kpiKey);
        });
      }
    );

    return emptyResponses;
  }

  private getNoDataKpi(kpiKey: KpiKeys): Kpi {
    return {
      name: STUDY_KPI_KEYS_TO_NAMES_MAP[kpiKey]!,
      type: STUDY_KPI_KEYS_TO_TYPE_MAPPING[kpiKey]!,
      trendText: 'No trend data available',
      value: undefined,
      numerator: undefined,
      denominator: undefined,
      health: undefined
    };
  }
  private getKpiByKey(kpiKey: KpiKeys): Kpi | undefined {
    for (const kpisByKey of Object.values(this.allKpisByCategory) as KpisByKey[]) {
      for (const [key, value] of Object.entries(kpisByKey) as [KpiKeys, Kpi][]) {
        if (key === kpiKey) {
          return value;
        }
      }
    }
    return;
  }

  private kpiHasValidValue(kpi: Kpi): boolean {
    return kpi.value !== undefined;
  }
}
